import React, { useMemo } from "react";
import { View, StyleSheet, TouchableOpacity, Text } from "react-native";
import NtActivityIndicator from "./NtActivityIndicator";
import NtMaterialIcon from "./NtMaterialIcon";
import { useTheme } from "../context/ThemeContext";

/**
 * Primary action button. Theme-driven (Direction C).
 *
 * Props (backward-compatible):
 *   text, textColor, icon, onPress, containerStyle, textStyle, isLoading
 * New:
 *   variant: 'primary' | 'secondary' | 'ghost' (default: 'primary')
 *   size:    'sm' | 'md' (default: 'md')
 */
function NtButton({
  text,
  textColor,
  icon,
  onPress,
  containerStyle,
  textStyle,
  isLoading = false,
  variant = "primary",
  size = "md",
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme, variant, size), [theme, variant, size]);

  const color = textColor || styles.resolvedTextColor;

  const handleClick = () => {
    if (isLoading) return;
    onPress && onPress();
  };

  return (
    <TouchableOpacity
      style={[styles.button, containerStyle]}
      onPress={handleClick}
      activeOpacity={0.85}
    >
      {icon && (
        <NtMaterialIcon
          style={styles.icon}
          name={icon}
          size={size === "sm" ? 16 : 18}
          color={color}
        />
      )}
      {text && (
        <Text style={[styles.text, { color }, textStyle]}>{text}</Text>
      )}
      {isLoading && (
        <NtActivityIndicator
          containerStyle={{ marginLeft: 8 }}
          size="small"
          color={color}
        />
      )}
    </TouchableOpacity>
  );
}

const makeStyles = (t, variant, size) => {
  const padV = size === "sm" ? 6 : 9;
  const padH = size === "sm" ? 10 : 14;
  const fontSize = size === "sm" ? 12 : 13;

  const base = {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: padV,
    paddingHorizontal: padH,
    borderRadius: t.radius,
    gap: 6,
  };

  let style, resolvedTextColor;
  if (variant === "primary") {
    style = { ...base, backgroundColor: t.brand, borderWidth: 1, borderColor: t.brand };
    resolvedTextColor = "#ffffff";
  } else if (variant === "ghost") {
    style = { ...base, backgroundColor: "transparent", borderWidth: 1, borderColor: "transparent" };
    resolvedTextColor = t.textDim;
  } else {
    // secondary / default outlined
    style = { ...base, backgroundColor: t.surface2, borderWidth: 1, borderColor: t.border };
    resolvedTextColor = t.text;
  }

  return {
    ...StyleSheet.create({
      button: style,
      text: { fontSize, fontWeight: "500" },
      icon: {},
    }),
    resolvedTextColor,
  };
};

export default NtButton;
